import { useState } from "react";
import { Button, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography } from "@mui/material";
import { createEntityStore, UseEntityStore } from "../../common/entityStore";
import { renderClose } from "../../common/dialog";
import { ProductDto, ProjectDto, ProjectOfferDto, ProjectOfferEditDto } from "./common";
import { EnumItemDto, moOfferTypes, OfferTypeId, offerTypes } from "../../common/enum";
import { propBoolField, propCustomEnumField, propNumberField, propSimpleEnumField, propTextField } from "../../common/entityStoreForm";
import { createListStore, UseListStore } from "../../common/listStore";
import { StoreWrapper } from "../../common/store";
import { combineLatest, map } from "rxjs";
import { useStateWithDeps } from "use-state-with-deps";
import { propInt, propMinValue, propRequired, propValidValue, propValueValidator } from "../../common/entityStoreValidators";
import { NewMo } from "./ProjectOfferDialog.NewMo";
import { SaleArticleDto } from "../../common/order";

export function ProjectOfferDialog(props: {
    close: () => void;
    product: ProductDto;
    project: ProjectDto;
    projectOffer: ProjectOfferDto | null;
    projectOfferChanged(projectOffer: ProjectOfferDto): void;
    useProjectOfferListStore: UseListStore<ProjectOfferDto>;
    useSaleArticleListStore: UseListStore<SaleArticleDto>;
    isMailProduct: boolean;
}) {
    const [useProjectOfferStore] = useState(() => createEntityStore<ProjectOfferEditDto>({
        path: "products/projectOffers", qs: { projectId: `${props.project.id}` },
        saveProps: ["offerNumber", "isAct", "description", "ofrLetter", "ofrCsLetter",
            "offerType", "offerShipmentUnit", "offerProduct", "offerProject", "offerMailOrderShipmentId", "offerPrice",
            "critMaxActiveUse", "critMaxActiveValue",
            "critMinPayUse", "critMinPayVal",
            "critMaxPayUse", "critMaxPayVal",
            "critMaxOffUse", "critMaxOffVal", "critLastDaysVal",
            "critMaxSameUse", "critMaxSameVal", "critLastDaysSameVal",
            "critSaleArticleId",
        ],
        id: props.projectOffer != null ? props.projectOffer.id : null,
        getNewId: p => p.id!,
        data: props.projectOffer != null ? props.projectOffer : newOffer,
        fetchOnInit: false,
        onSaved(p) {
            props.projectOfferChanged(p as ProjectOfferDto);
            props.close();
        },
        propValidators: [
            propValidValue("offerNumber", "offerNumberFormatted"),
            propRequired("offerNumber"),
            propInt("offerNumber"),
            propValueValidator("offerNumber", (p, e) => p != null &&
                props.useProjectOfferListStore.getState().items.some(r => r.id !== e.id && r.offerNumber === p) && "This seq. number already exists."),

            propRequired("description"),
            propRequired("ofrLetter"),
            !props.isMailProduct && propRequired("ofrCsLetter"),
            propRequired("offerType", undefined, p => p !== OfferTypeId.None),

            propValueValidator("offerShipmentUnit", (p, e) => e.offerType === OfferTypeId.SuChange && !(p > 0) && "Required field."),
            propValueValidator("offerProject", (p, e) => e.offerType === OfferTypeId.NewCo && p == null && "Required field."),
            propValueValidator("offerMailOrderShipmentId", (p, e) => e.offerType === OfferTypeId.NewMo && p == null && "Required field."),
            propValueValidator("offerPrice", (p, e) => e.offerType !== OfferTypeId.None && !(p > 0) && "Value must be more than 0."),

            propValidValue("critMaxActiveValue", "critMaxActiveValueFormatted"),
            propRequired("critMaxActiveValue"),
            propMinValue("critMaxActiveValue", 0),

            propValidValue("critMinPayVal", "critMinPayValFormatted"),
            propRequired("critMinPayVal"),
            propMinValue("critMinPayVal", 0),

            propValidValue("critMaxPayVal", "critMaxPayValFormatted"),
            propRequired("critMaxPayVal"),
            propMinValue("critMaxPayVal", 0),

            propValidValue("critMaxOffVal", "critMaxOffValFormatted"),
            propRequired("critMaxOffVal"),
            propMinValue("critMaxOffVal", 0),
            propValidValue("critLastDaysVal", "critLastDaysValFormatted"),
            propRequired("critLastDaysVal"),
            propMinValue("critLastDaysVal", 0),

            propValidValue("critMaxSameVal", "critMaxSameValFormatted"),
            propRequired("critMaxSameVal"),
            propMinValue("critMaxSameVal", 0),
            propValidValue("critLastDaysSameVal", "critLastDaysSameValFormatted"),
            propRequired("critLastDaysSameVal"),
            propMinValue("critLastDaysSameVal", 0),
        ]
    }));
    var projectOfferStore = useProjectOfferStore(p => ({
        save: p.save,
        isSaveDisabled: p.isSaveDisabled,
        entityId: p.entityId,
    }));

    function resetOfferType(offerType: OfferTypeId, oldOfferType: OfferTypeId) {
        if (oldOfferType === OfferTypeId.SuChange) {
            useProjectOfferStore.getState().setClientDataProp("offerShipmentUnit", 0);
        } else if (oldOfferType === OfferTypeId.NewCo) {
            useProjectOfferStore.getState().setClientDataProp("offerProduct", null);
            useProjectOfferStore.getState().setClientDataProp("offerProject", null);
        } else if (oldOfferType === OfferTypeId.NewMo) {
            useProjectOfferStore.getState().setClientDataProp("offerMailOrderShipmentId", null);
        }

        if (offerType === OfferTypeId.None) {
            useProjectOfferStore.getState().setClientDataProp("offerPrice", 0);
            useProjectOfferStore.getState().setClientDataProp("offerPriceFormatted", undefined);
        }
    }

    return <>
        <DialogTitle>{projectOfferStore.entityId == null ? "New offer" : "Edit offer"}</DialogTitle>
        {renderClose(props.close)}
        <DialogContent>
            <Grid container columnSpacing={4} alignItems="stretch">
                <Grid item container columnSpacing={2} xs={12} alignSelf="flex-start">
                    <Grid item xs={props.project.isDefault ? 6 : 3}><TextField label="Product" value={props.product.code + " " + props.product.name} disabled /></Grid>
                    {!props.project.isDefault && <Grid item xs={3}><TextField label="Project" value={props.project.code + " " + props.project.name} disabled /></Grid>}
                    <Grid item xs={3}>{propNumberField(useProjectOfferStore)("offerNumber", "offerNumberFormatted", "Seq. number")}</Grid>
                    <Grid item xs={3}>{propBoolField(useProjectOfferStore)("isAct", "Active")}</Grid>
                    <Grid item xs={3}>{propTextField(useProjectOfferStore)("description", "Description")}</Grid>
                    <Grid item xs={3}>{propTextField(useProjectOfferStore)("ofrLetter", "Letter")}</Grid>
                    {!props.isMailProduct && <Grid item xs={3}>{propTextField(useProjectOfferStore)("ofrCsLetter", "CS letter")}</Grid>}
                </Grid>
                <Grid item container columnSpacing={2} xs={6} alignSelf="flex-start">
                    <Grid item xs={12}><Typography variant="h6">Special offer</Typography></Grid>
                    <Grid item xs={12}>{propSimpleEnumField(useProjectOfferStore)("offerType", "Offer type", props.isMailProduct ? moOfferTypes : offerTypes, {
                        onChange: (newValue, oldValue) => resetOfferType(newValue, oldValue),
                    })}</Grid>
                    <StoreWrapper useStore={() => useProjectOfferStore} render={useProjectOfferStore => {
                        const offerType = useProjectOfferStore(p => p.clientData.offerType);
                        if (offerType === OfferTypeId.SuChange) {
                            return <>
                                <Grid item xs={12}>{propSimpleEnumField(useProjectOfferStore)("offerShipmentUnit", "Offer SU", offerSuOptions)}</Grid>
                                <Grid item xs={12}>{propNumberField(useProjectOfferStore)("offerPrice", "offerPriceFormatted", "Offer price", { isCurrency: true })}</Grid>
                            </>
                        } else if (offerType === OfferTypeId.NewCo) {
                            return <NewCo useProjectOfferStore={useProjectOfferStore} />;
                        } else if (offerType === OfferTypeId.NewMo) {
                            return <NewMo useProjectOfferStore={useProjectOfferStore} useSaleArticleListStore={props.useSaleArticleListStore} />;
                        } else {
                            return null;
                        }
                    }} />
                </Grid>
                <Grid item container columnSpacing={2} xs={6} alignSelf="flex-start">
                    <Grid item xs={12}><Typography variant="h6">Offer criteria</Typography></Grid>
                    <Grid item xs={5}></Grid>
                    <Grid item xs={5}>{propNumberField(useProjectOfferStore)("critMaxActiveValue", "critMaxActiveValueFormatted", "Maximum number of active orders")}</Grid>
                    <Grid item xs={2}>{propBoolField(useProjectOfferStore)("critMaxActiveUse", "Use")}</Grid>

                    <Grid item xs={5}></Grid>
                    <Grid item xs={5}>{propNumberField(useProjectOfferStore)("critMinPayVal", "critMinPayValFormatted", "Minimum paid amount")}</Grid>
                    <Grid item xs={2}>{propBoolField(useProjectOfferStore)("critMinPayUse", "Use")}</Grid>

                    <Grid item xs={5}></Grid>
                    <Grid item xs={5}>{propNumberField(useProjectOfferStore)("critMaxPayVal", "critMaxPayValFormatted", "Maximum paid amount")}</Grid>
                    <Grid item xs={2}>{propBoolField(useProjectOfferStore)("critMaxPayUse", "Use")}</Grid>

                    <Grid item xs={5}>{propNumberField(useProjectOfferStore)("critMaxOffVal", "critMaxOffValFormatted", "Maximum number of offers")}</Grid>
                    <Grid item xs={5}>{propNumberField(useProjectOfferStore)("critLastDaysVal", "critLastDaysValFormatted", "for last", { unit: "days" })}</Grid>
                    <Grid item xs={2}>{propBoolField(useProjectOfferStore)("critMaxOffUse", "Use")}</Grid>

                    <Grid item xs={5}>{propNumberField(useProjectOfferStore)("critMaxSameVal", "critMaxSameValFormatted", "Maximum number of same offers")}</Grid>
                    <Grid item xs={5}>{propNumberField(useProjectOfferStore)("critLastDaysSameVal", "critLastDaysSameValFormatted", "for last", { unit: "days" })}</Grid>
                    <Grid item xs={2}>{propBoolField(useProjectOfferStore)("critMaxSameUse", "Use")}</Grid>

                    {props.isMailProduct && <Grid item xs={12}>{propCustomEnumField(useProjectOfferStore)("critSaleArticleId", "Sale article in MO", props.useSaleArticleListStore, {
                        getId: p => p.id, getName: p => p.code + " " + p.name
                    })}</Grid>}
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            <Button onClick={projectOfferStore.save} disabled={projectOfferStore.isSaveDisabled} color="save">Save</Button>
            <Button onClick={props.close} color="negative" variant="outlined">Close</Button>
        </DialogActions>
    </>;
}

function NewCo(props: {
    useProjectOfferStore: UseEntityStore<ProjectOfferEditDto>;
}) {
    const [{ useProductsStore, useProjectsStore }] = useStateWithDeps(() => {
        const useProductsStore = createListStore<ProductListItemDto>({ path: "products/projectOffers/productsAndProjects" });
        const useProjectsStore = createListStore<ProjectListItemDto>({
            data: combineLatest([useProductsStore.getState().items$, props.useProjectOfferStore.getState().clientData$]).pipe(map(([products, offer]) => ({
                items: products.items.find(p => p.id === offer.data.offerProduct)?.projects || [],
                isFetching: products.isFetching
            })))
        });
        return { useProductsStore, useProjectsStore };
    }, [props.useProjectOfferStore]);
    return <>
        <Grid item xs={12}>{propCustomEnumField(props.useProjectOfferStore)("offerProduct", "Offer product", useProductsStore, {
            getId: p => p.id, getName: p => `${p.code} ${p.name}`, onChange: () => {
                props.useProjectOfferStore.getState().setClientDataProp("offerProject", null);
            }
        })}</Grid>
        <Grid item xs={12}>{propCustomEnumField(props.useProjectOfferStore)("offerProject", "Offer project", useProjectsStore,
            { getId: p => p.id, getName: p => `${p.code} ${p.name}` })}</Grid>
        <Grid item xs={12}>{propNumberField(props.useProjectOfferStore)("offerPrice", "offerPriceFormatted", "Offer price", { isCurrency: true })}</Grid>
    </>;
}

const newOffer: ProjectOfferEditDto = {
    id: null,
    offerNumber: null,
    isAct: true,
    description: "",
    ofrLetter: "",
    ofrCsLetter: "",
    offerType: OfferTypeId.None,
    offerShipmentUnit: 0,
    offerProduct: null,
    offerProject: null,
    offerPrice: 0,
    offerMailOrderShipmentId: null,
    critMaxActiveUse: false,
    critMaxActiveValue: 0,
    critMinPayUse: false,
    critMinPayVal: 0,
    critMaxPayUse: false,
    critMaxPayVal: 0,
    critMaxOffUse: false,
    critMaxOffVal: 0,
    critLastDaysVal: 0,
    critMaxSameUse: false,
    critMaxSameVal: 0,
    critLastDaysSameVal: 0,
    critSaleArticleId: null,
};

interface ProductListItemDto {
    id: number;
    code: string;
    name: string;
    projects: ProjectListItemDto[];
}

interface ProjectListItemDto {
    id: number;
    code: string;
    name: string;
}

const offerSuOptions: EnumItemDto[] = [
    { id: 2, name: "2" },
    { id: 3, name: "3" },
];
